import React from "react";
import { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import yellowFacesLogo from "../images/yf.svg";
import invertedCommaLogo from "../images/ic.svg";
import equatorsLogo from "../images/eq.svg";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const VOTING_CATEGORIES = [
  "Research & Understanding",
  "Design & Execution",
  "Presentation & Articulation",
  "Disruptive Idea",
];

function Main() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  // const [selectedTeam, setSelectedTeam] = useState(null);
  const [ratings, setRatings] = useState({});
  const [showVoting, setShowVoting] = useState(false);
  const [topics, setTopics] = useState("");
  const [comments, setComments] = useState({});

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (loggedInUser) => {
      if (loggedInUser) {
        setUser(loggedInUser);
        await syncProjects(loggedInUser.email);
        await filterProjects(loggedInUser.email);
      } else {
        navigate("/");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const syncProjects = async (userEmail) => {
    const userDocRef = doc(db, "users", userEmail);
    const userDocSnap = await getDoc(userDocRef);
    const projectsRef = collection(db, "Projects");
    const projectDocs = await getDocs(projectsRef);

    const allProjects = projectDocs.docs.map((doc) => ({
      id: doc.id,
      name: doc.data().name,
      hasVoted: false,
      team: null,
      votes: {},
    }));

    if (!userDocSnap.exists() || !userDocSnap.data().projectsList) {
      await setDoc(userDocRef, { projectsList: allProjects }, { merge: true });
    } else {
      const userProjects = userDocSnap.data().projectsList || [];
      const userProjectIds = userProjects.map((p) => p.id);

      const newProjects = allProjects.filter(
        (p) => !userProjectIds.includes(p.id),
      );

      if (newProjects.length > 0) {
        await updateDoc(userDocRef, {
          projectsList: [...userProjects, ...newProjects],
        });
      }
    }
  };

  const filterProjects = async (userEmail) => {
    const userDocRef = doc(db, "users", userEmail);
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists() && userDocSnap.data().projectsList) {
      const filteredProjects = userDocSnap
        .data()
        .projectsList.filter((p) => !p.hasVoted);

      setProjects(filteredProjects);
    }
  };

  const handleCommentChange = (team, value) => {
    setComments((prev) => ({
      ...prev,
      [team]: value,
    }));
  };

  const handleVote = async (projectId) => {
    if (!user) {
      toast.error("You need to be logged in to vote!");
      return;
    }

    try {
      const userDocRef = doc(db, "users", user.email);
      const userDocSnap = await getDoc(userDocRef);

      const projectDocRef = doc(db, "Projects", projectId);
      const projectDocSnap = await getDoc(projectDocRef);

      if (!userDocSnap.exists() || !projectDocSnap.exists()) {
        toast.error("Something went wrong. Please try again.");
        return;
      }

      const projectsList = userDocSnap.data().projectsList || [];
      let votesData = projectDocSnap.data().votes || {};

      const teamsToVote = ["equators", "yellowFaces", "invertedComma"]; // Vote for ALL teams

      let userVotesData = {};

      teamsToVote.forEach((team) => {
        let teamRatings = {};
        let total = 0;

        // 🔹 Dynamic 4-category calculation
        VOTING_CATEGORIES.forEach((category) => {
          const value = ratings[`${team}-${category}`] || 0;
          teamRatings[category] = value;
          total += value;
        });

        const avgRounded = parseFloat(
          (total / VOTING_CATEGORIES.length).toFixed(2),
        );

        userVotesData[team] = { ...teamRatings, avg: avgRounded };

        if (!votesData[team]) votesData[team] = { avg: 0 };

        votesData[team][user.email] = {
          avg: avgRounded,
          ratings: teamRatings,
          comment: comments[team] || "",
        };

        const allRatings = Object.entries(votesData[team])
          .filter(([key]) => key !== "avg")
          .map(([_, value]) => value.avg);

        votesData[team].avg = parseFloat(
          (
            allRatings.reduce((sum, rating) => sum + rating, 0) /
            allRatings.length
          ).toFixed(2),
        );
      });

      const updatedProjects = projectsList.map((p) =>
        p.id === projectId ? { ...p, hasVoted: true, votes: userVotesData } : p,
      );

      await updateDoc(userDocRef, { projectsList: updatedProjects });
      await updateDoc(projectDocRef, { votes: votesData });

      setProjects(updatedProjects.filter((p) => !p.hasVoted));
      setSelectedProject(null);
      setRatings({});
      setComments({});

      toast.success("Your vote has been submitted successfully! 🎉");
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while submitting your vote.");
    }
  };

  const isCommentRequired = (team) =>
    VOTING_CATEGORIES.some(
      (category) =>
        ratings[`${team}-${category}`] !== undefined &&
        ratings[`${team}-${category}`] <= 2,
    );

  // Check if voting is complete and required comments are filled
  const isVotingComplete = () => {
    const teams = ["equators", "yellowFaces", "invertedComma"];

    for (let team of teams) {
      for (let category of VOTING_CATEGORIES) {
        if (!ratings[`${team}-${category}`]) return false;
      }
      if (isCommentRequired(team) && !comments[team]) return false;
    }
    return true;
  };

  const handleTopicsChange = (e) => {
    setTopics(e.target.value);
  };

  const fetchUserName = async (email) => {
    const userRef = doc(db, "users", email); // Reference by email
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return userSnap.data().fullName; // Retrieve full name from Firestore
    } else {
      return null;
    }
  };

  const handleShareTopics = async () => {
    if (!topics.trim()) {
      toast.info("Please enter topics before submitting.");
      return;
    }

    // Convert comma-separated topics into an array & remove extra spaces
    const topicsArray = topics
      .split(",")
      .map((topic) => topic.trim())
      .filter((topic) => topic);

    if (topicsArray.length === 0) {
      toast.info("Please enter valid topics.");
      return;
    }

    const topicsDocRef = doc(db, "Topics", "sharedTopics"); // Single document for all users

    const user = auth.currentUser;
    if (!user) {
      toast.error("User is not authenticated!");
      return;
    }

    let userName = user.displayName; // Check if displayName exists
    if (!userName) {
      userName = await fetchUserName(user.email); // Fetch from Firestore if null
    }

    if (!userName) {
      toast.error("Full name not found in user data!");
      return;
    }

    try {
      const topicsDocSnap = await getDoc(topicsDocRef);

      if (topicsDocSnap.exists()) {
        const existingData = topicsDocSnap.data();
        const userTopics = existingData[userName] || [];
        const updatedTopics = Array.from(
          new Set([...userTopics, ...topicsArray]),
        ); // Merge & remove duplicates

        await updateDoc(topicsDocRef, {
          [userName]: updatedTopics,
          timestamp: new Date(),
        });

        toast.success("Topics updated successfully!");
      } else {
        await setDoc(topicsDocRef, {
          [userName]: topicsArray,
          timestamp: new Date(),
        });

        toast.success("Topics shared successfully!");
      }

      setTopics(""); // Clear input field
    } catch (err) {
      // console.error("Error sharing topics:", err);
      toast.error("Error sharing topics: " + err.message);
    }
  };

  return (
    <div className="flex flex-col">
      <ToastContainer />
      <nav className="z-[100] fixed top-0 left-0 w-full bg-[#3c603c] text-[#f5f7df] shadow-md py-4 px-6 flex justify-between items-center">
        <h1 className="text-xl font-bold">
          {user?.displayName || "Dashboard"}
        </h1>
        <button
          onClick={() => navigate("/")}
          className="bg-[#f5f7df] text-[#3c603c] px-4 py-2 rounded-md"
        >
          Logout
        </button>
      </nav>

      <div className=" flex-grow flex flex-col items-center mt-24 px-6">
        <h2 className="text-2xl font-semibold mb-6 text-[#3c603c]">
          Your Projects
        </h2>

        <div className="flex gap-4">
          {projects.filter((project) => !project.hasVoted).length > 0 ? (
            projects
              .filter((project) => !project.hasVoted)
              .map((project) => (
                <button
                  key={project.id}
                  className={`px-6 py-3 rounded-lg transition-all duration-200 ${
                    selectedProject === project.id
                      ? "bg-[#f5f7df] text-[#3c603c] shadow-inner border border-[#3c603c] scale-95"
                      : `bg-[#3c603c] text-[#f5f7df] hover:bg-[#f5f7df] hover:text-[#3c603c] border border-transparent 
                ${
                  showVoting && selectedProject !== project.id
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`
                  }`}
                  onClick={() => !showVoting && setSelectedProject(project.id)}
                  disabled={showVoting && selectedProject !== project.id}
                >
                  {project.name}
                </button>
              ))
          ) : (
            <p className="text-[#3c603c] text-lg font-semibold">
              No Pending Projects to Vote
            </p>
          )}
        </div>

        {selectedProject && (
          <div className="mt-6 flex flex-col items-center">
            <h2 className="text-xl font-semibold text-[#3c603c] mb-4 text-center">
              Please Make Sure You Do Ethical Voting
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-screen-lg px-4">
              {[
                { name: "equators", logo: equatorsLogo },
                { name: "yellowFaces", logo: yellowFacesLogo },
                { name: "invertedComma", logo: invertedCommaLogo },
              ].map((team) => (
                <div
                  key={team.name}
                  className="bg-white p-6 rounded-lg shadow-lg w-full md:w-80 max-w-full"
                >
                  <h3 className="text-3xl font-semibold text-[#3c603c] flex items-center gap-2">
                    <img
                      src={team.logo}
                      alt={team.name}
                      className="w-24 h-24 md:w-36 md:h-36 object-contain"
                    />
                  </h3>

                  {VOTING_CATEGORIES.map((category) => (
                    <div
                      key={category}
                      className="flex justify-between items-center mt-3"
                    >
                      <span className="text-[#3c603c]">{category}</span>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            className={`text-2xl ${
                              ratings[`${team.name}-${category}`] >= star
                                ? "text-yellow-500"
                                : "text-gray-300"
                            }`}
                            onClick={() =>
                              setRatings({
                                ...ratings,
                                [`${team.name}-${category}`]: star,
                              })
                            }
                          >
                            ★
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Comment Box */}
                  <textarea
                    className="w-full p-2 mt-4 border rounded-lg resize-none"
                    placeholder="Add a comment (if required)..."
                    value={comments[team.name] || ""}
                    onChange={(e) =>
                      handleCommentChange(team.name, e.target.value)
                    }
                  />

                  {isCommentRequired(team.name) && (
                    <p className="text-red-500 text-sm mt-1">
                      A comment is required for this team due to a low rating.
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Submit Button */}
            <button
              onClick={() => {
                handleVote(selectedProject);
                setShowVoting(false);
              }}
              disabled={!isVotingComplete()}
              className={`mt-6 px-6 py-3 rounded-lg border transition-all w-full max-w-xs ${
                isVotingComplete()
                  ? "bg-[#3c603c] text-[#f5f7df] hover:bg-[#f5f7df] hover:text-[#3c603c] border-[#3c603c]"
                  : "bg-gray-300 text-gray-600 border-gray-400 cursor-not-allowed"
              }`}
            >
              Submit Vote
            </button>
          </div>
        )}
      </div>

      <div className="flex justify-center items-center m-10">
        <div className="bg-[#3c603c] p-4 rounded-xl shadow-md w-[350px] text-center">
          <label className="block text-[#f5f7df] text-base font-medium mb-2">
            Enter Topics
          </label>
          <textarea
            value={topics}
            onChange={handleTopicsChange}
            rows="3"
            className="w-full p-2 text-[#3c603c] bg-[#f5f7df] border border-[#f5f7df] rounded-md outline-none focus:ring-2 focus:ring-[#f5f7df] placeholder:text-gray-700 text-sm"
            placeholder="Enter topics separated by commas..."
          />
          <button
            onClick={handleShareTopics}
            className="mt-3 w-full py-2 text-sm font-medium bg-[#f5f7df] text-[#3c603c] rounded-md shadow transition-all hover:bg-[#3c603c] hover:text-[#f5f7df] border border-[#f5f7df]"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}

export default Main;
